import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button, confirmDelete, notifyError, cn, type Tone } from "../../../components/ui";
import { useBasesLocacionesStore, BASE_TYPES, BASE_STATUSES, type Base, type BaseStatus, type BaseType } from "../basesStore";
import { useBasesStore } from "../store";
import { apiErrorMessage } from "../services/basesApi";
import BaseFormModal from "../components/BaseFormModal";
import SiteMap from "../components/SiteMap";
import {
  DetailDrawer,
  DetailRow,
  FilterSelect,
  Kpi,
  LoadBlock,
  PersonAvatar,
  Photo,
  PhotoGallery,
  ProgressBar,
  SearchBox,
  SiteTree,
  Toolbar,
  ViewSwitch,
  pctTone,
  relativeTime,
  type TreeNode,
  type ViewKind,
} from "../components/console";

// ---------------------------------------------------------------------------
// Bases — consola de sitios estilo iVMS-4200: árbol de tipos/bases/zonas a la
// izquierda, KPIs del API, barra de herramientas (búsqueda, filtros, vista y
// mapa), tarjetas con foto real de portada y drawer de detalle con galería.
// ---------------------------------------------------------------------------

export const TYPE_ICONS: Record<BaseType, string> = {
  cedis: "ri-truck-line",
  patio: "ri-parking-box-line",
  oficina: "ri-building-2-line",
  bodega: "ri-store-3-line",
  coworking: "ri-team-line",
  estacionamiento: "ri-car-line",
};

export const STATUS_TONES: Record<BaseStatus, Tone> = {
  active: "success",
  opening: "info",
  maintenance: "warning",
  inactive: "neutral",
};

const TYPE_COLORS: Record<BaseType, string> = {
  cedis: "#0ea5e9",
  patio: "#22c55e",
  oficina: "#6366f1",
  bodega: "#f59e0b",
  coworking: "#ec4899",
  estacionamiento: "#a855f7",
};

const occPct = (b: Base) => (b.capacity ? Math.round((Math.min(b.occupancy, b.capacity) / b.capacity) * 100) : 0);

export default function BasesList() {
  const { t, i18n } = useTranslation("bases");
  const { bases, staff, summary, status, error, hydrate, addBase, updateBase, removeBase } = useBasesLocacionesStore();
  const amenities = useBasesStore((s) => s.amenities);

  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<BaseType | "">("");
  const [statusFilter, setStatusFilter] = useState<BaseStatus | "">("");
  const [treeSel, setTreeSel] = useState<string | null>(null);
  const [view, setView] = useState<ViewKind>("grid");
  const [showMap, setShowMap] = useState(true);
  const [selected, setSelected] = useState<Base | null>(null);
  const [editing, setEditing] = useState<Base | null | "new">(null);

  const tree: TreeNode[] = useMemo(
    () =>
      BASE_TYPES.filter((type) => bases.some((b) => b.type === type)).map((type) => ({
        id: `type:${type}`,
        label: t(`types.${type}`),
        icon: TYPE_ICONS[type],
        count: bases.filter((b) => b.type === type).length,
        children: bases
          .filter((b) => b.type === type)
          .map((b) => ({
            id: `base:${b.id}`,
            label: b.name,
            dotTone: STATUS_TONES[b.status],
            children: b.zones.map((z) => ({ id: `zone:${b.id}:${z.id}`, label: z.name, icon: "ri-shape-line", count: z.capacity || undefined })),
          })),
      })),
    [bases, t],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return bases.filter((b) => {
      if (treeSel?.startsWith("type:") && b.type !== treeSel.slice(5)) return false;
      if ((treeSel?.startsWith("base:") || treeSel?.startsWith("zone:")) && !treeSel.includes(`:${b.id}`)) return false;
      if (typeFilter && b.type !== typeFilter) return false;
      if (statusFilter && b.status !== statusFilter) return false;
      if (!q) return true;
      return [b.name, b.code, b.city, b.state, b.address, b.manager?.name].some((v) => String(v ?? "").toLowerCase().includes(q));
    });
  }, [bases, query, typeFilter, statusFilter, treeSel]);

  const onTreeSelect = (id: string | null) => {
    setTreeSel(id);
    if (id?.startsWith("base:") || id?.startsWith("zone:")) {
      const b = bases.find((x) => id.includes(`:${x.id}`));
      if (b) setSelected(b);
    }
  };

  const save = async (draft: Omit<Base, "id" | "staffCount" | "updatedAt">, files: File[]) => {
    if (editing && editing !== "new") {
      const saved = await updateBase(editing.id, draft, files);
      if (selected?.id === saved.id) setSelected({ ...saved, staffCount: selected.staffCount });
    } else {
      await addBase(draft, files);
    }
  };

  const remove = async (b: Base) => {
    const ok = await confirmDelete({ title: t("list.deleteTitle", { name: b.name }), text: t("list.deleteText"), cancelText: t("common.cancel"), confirmText: t("common.delete") });
    if (!ok) return;
    try {
      await removeBase(b.id);
      setSelected(null);
    } catch (err) {
      notifyError(apiErrorMessage(err, t("common.saveError")));
    }
  };

  const mapPoints = useMemo(
    () =>
      filtered
        .filter((b) => typeof b.latitude === "number" && typeof b.longitude === "number")
        .map((b) => ({ id: b.id, lat: b.latitude as number, lng: b.longitude as number, label: `${b.name} · ${b.city ?? ""}`, color: TYPE_COLORS[b.type] })),
    [filtered],
  );

  const roundsPct = summary?.rounds.compliancePctToday ?? null;

  return (
    <div className="flex h-full w-full overflow-hidden">
      <SiteTree title={t("list.treeTitle")} allLabel={t("list.allBases")} nodes={tree} selected={treeSel} onSelect={onTreeSelect} />

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Toolbar>
          <div className="mr-2 flex items-center gap-2">
            <i className="ri-map-pin-2-line text-lg text-primary-500" />
            <h1 className="text-sm font-semibold text-content">{t("list.title")}</h1>
          </div>
          <SearchBox value={query} onChange={setQuery} placeholder={t("list.search")} />
          <FilterSelect label={t("fields.type")} value={typeFilter} onChange={setTypeFilter} options={BASE_TYPES.map((k) => ({ value: k, label: t(`types.${k}`) }))} />
          <FilterSelect label={t("fields.status")} value={statusFilter} onChange={setStatusFilter} options={BASE_STATUSES.map((k) => ({ value: k, label: t(`statuses.${k}`) }))} />
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant={showMap ? "soft" : "ghost"} icon={<i className="ri-map-2-line" />} onClick={() => setShowMap((v) => !v)}>
              {t("list.map")}
            </Button>
            <ViewSwitch value={view} onChange={setView} />
            <Button size="sm" variant="ghost" iconOnly icon={<i className="ri-refresh-line" />} aria-label={t("common.refresh")} title={t("common.refresh")} onClick={() => void hydrate()} />
            <Button size="sm" icon={<i className="ri-add-line" />} onClick={() => setEditing("new")}>
              {t("list.new")}
            </Button>
          </div>
        </Toolbar>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <Kpi icon="ri-map-pin-2-line" label={t("kpi.bases")} value={summary?.bases ?? bases.length} hint={t("kpi.activeOf", { n: summary?.activeBases ?? bases.filter((b) => b.status === "active").length })} />
            <Kpi icon="ri-pie-chart-2-line" label={t("kpi.occupancy")} value={`${summary?.occupancyPct ?? 0}%`} progress={summary?.occupancyPct ?? 0} tone={pctTone(summary?.occupancyPct, true)} hint={t("kpi.occupancyHint", { occ: (summary?.occupancy ?? 0).toLocaleString(), cap: (summary?.capacity ?? 0).toLocaleString() })} />
            <Kpi icon="ri-calendar-check-line" label={t("kpi.bookingsToday")} value={summary?.bookingsToday ?? 0} tone="info" hint={t("kpi.pendingBookings", { n: summary?.pendingBookings ?? 0 })} />
            <Kpi icon="ri-shield-check-line" label={t("kpi.roundsToday")} value={roundsPct === null ? "—" : `${roundsPct}%`} progress={roundsPct} tone={pctTone(roundsPct)} hint={t("kpi.roundsHint", { done: summary?.rounds.completedToday ?? 0, total: summary?.rounds.today ?? 0 })} />
            <Kpi icon="ri-alarm-warning-line" label={t("kpi.openIncidents")} value={summary?.openIncidents ?? 0} tone={(summary?.openIncidents ?? 0) > 0 ? "warning" : "success"} hint={t("kpi.week", { pct: summary?.rounds.compliancePctWeek ?? "—" })} />
            <Kpi icon="ri-team-line" label={t("kpi.staff")} value={summary?.staff ?? staff.length} tone="primary" hint={t("kpi.amenities", { n: summary?.amenities ?? amenities.length })} />
          </div>

          <LoadBlock
            status={status}
            error={error}
            onRetry={() => void hydrate()}
            empty={bases.length === 0}
            emptyIcon="ri-map-pin-add-line"
            emptyTitle={t("list.emptyTitle")}
            emptyText={t("list.emptyText")}
            emptyAction={
              <Button icon={<i className="ri-add-line" />} onClick={() => setEditing("new")}>
                {t("list.new")}
              </Button>
            }
          >
            {showMap && mapPoints.length > 0 ? (
              <div className="mb-4 overflow-hidden rounded-xl border border-line">
                <SiteMap points={mapPoints} selectedId={selected?.id ?? null} onSelect={(id) => setSelected(bases.find((b) => b.id === id) ?? null)} className="h-64 w-full" />
              </div>
            ) : null}

            {filtered.length === 0 ? (
              <p className="py-12 text-center text-sm text-content-muted">{t("list.noMatches")}</p>
            ) : view === "grid" ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {filtered.map((b) => (
                  <BaseCard key={b.id} base={b} onOpen={() => setSelected(b)} active={selected?.id === b.id} />
                ))}
              </div>
            ) : view === "list" ? (
              <div className="divide-y divide-line overflow-hidden rounded-xl border border-line bg-card">
                {filtered.map((b) => (
                  <button key={b.id} type="button" onClick={() => setSelected(b)} className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-subtle">
                    <Photo src={b.photos[0]} alt={b.name} thumb className="h-12 w-20 shrink-0 rounded-lg" icon={TYPE_ICONS[b.type]} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-semibold text-content">{b.name}</span>
                        <Badge size="sm" tone={STATUS_TONES[b.status]} dot>
                          {t(`statuses.${b.status}`)}
                        </Badge>
                      </div>
                      <div className="truncate text-xs text-content-secondary">
                        {t(`types.${b.type}`)} · {[b.city, b.state].filter(Boolean).join(", ")}
                      </div>
                    </div>
                    <div className="hidden w-40 sm:block">
                      <ProgressBar pct={occPct(b)} tone={pctTone(occPct(b), true)} />
                      <div className="mt-1 text-[11px] text-content-muted">{t("card.occupancy", { pct: occPct(b) })}</div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-line bg-card">
                <table className="w-full min-w-[760px] text-sm">
                  <thead className="bg-subtle text-left text-xs uppercase tracking-wide text-content-secondary">
                    <tr>
                      <th className="px-3 py-2">{t("fields.name")}</th>
                      <th className="px-3 py-2">{t("fields.type")}</th>
                      <th className="px-3 py-2">{t("fields.city")}</th>
                      <th className="px-3 py-2">{t("fields.status")}</th>
                      <th className="px-3 py-2 text-right">{t("fields.capacity")}</th>
                      <th className="px-3 py-2">{t("fields.occupancy")}</th>
                      <th className="px-3 py-2">{t("fields.manager")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {filtered.map((b) => (
                      <tr key={b.id} onClick={() => setSelected(b)} className="cursor-pointer hover:bg-subtle">
                        <td className="px-3 py-2 font-medium text-content">
                          {b.name} <span className="text-xs text-content-muted">{b.code}</span>
                        </td>
                        <td className="px-3 py-2 text-content-secondary">{t(`types.${b.type}`)}</td>
                        <td className="px-3 py-2 text-content-secondary">{b.city}</td>
                        <td className="px-3 py-2">
                          <Badge size="sm" tone={STATUS_TONES[b.status]} dot>
                            {t(`statuses.${b.status}`)}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-content">{b.capacity.toLocaleString()}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <ProgressBar pct={occPct(b)} tone={pctTone(occPct(b), true)} className="w-20" />
                            <span className="text-xs tabular-nums text-content-secondary">{occPct(b)}%</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-content-secondary">{b.manager?.name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </LoadBlock>
        </div>
      </div>

      <DetailDrawer
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        title={selected?.name}
        subtitle={selected ? `${t(`types.${selected.type}`)} · ${selected.code ?? ""}` : null}
        actions={
          selected ? (
            <>
              <Button size="sm" variant="soft" icon={<i className="ri-edit-line" />} onClick={() => setEditing(selected)}>
                {t("common.edit")}
              </Button>
              <Button size="sm" variant="dangerSoft" iconOnly icon={<i className="ri-delete-bin-line" />} aria-label={t("common.delete")} onClick={() => void remove(selected)} />
            </>
          ) : null
        }
      >
        {selected ? (
          <BaseDetail base={selected} staffNames={staff.filter((s) => s.base_id === selected.id)} amenityNames={amenities.filter((a) => a.base_id === selected.id).map((a) => a.name)} lng={i18n.language} />
        ) : null}
      </DetailDrawer>

      {editing ? <BaseFormModal base={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSave={save} /> : null}
    </div>
  );
}

function BaseCard({ base: b, onOpen, active }: { base: Base; onOpen: () => void; active: boolean }) {
  const { t } = useTranslation("bases");
  const pct = occPct(b);
  return (
    <button type="button" onClick={onOpen} className={cn("group flex flex-col overflow-hidden rounded-xl border bg-card text-left transition-shadow hover:shadow-lg", active ? "border-primary-500 ring-1 ring-primary-500" : "border-line")}>
      <div className="relative">
        <Photo src={b.photos[0]} alt={b.name} thumb className="aspect-[16/9] w-full" icon={TYPE_ICONS[b.type]} />
        <div className="absolute inset-x-0 top-0 flex items-start justify-between p-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur">
            <i className={TYPE_ICONS[b.type]} /> {t(`types.${b.type}`)}
          </span>
          <Badge size="sm" variant="solid" tone={STATUS_TONES[b.status]}>
            {t(`statuses.${b.status}`)}
          </Badge>
        </div>
        {b.photos.length > 1 ? (
          <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[11px] text-white">
            <i className="ri-image-line" /> {b.photos.length}
          </span>
        ) : null}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-3">
        <div>
          <div className="truncate text-sm font-semibold text-content">{b.name}</div>
          <div className="flex items-center gap-1 truncate text-xs text-content-secondary">
            <i className="ri-map-pin-line" /> {[b.city, b.state].filter(Boolean).join(", ") || "—"}
          </div>
        </div>
        <div>
          <div className="mb-1 flex justify-between text-[11px] text-content-muted">
            <span>{t("card.occupancy", { pct })}</span>
            <span className="tabular-nums">
              {b.occupancy.toLocaleString()} / {b.capacity.toLocaleString()} {b.capacityUnit}
            </span>
          </div>
          <ProgressBar pct={pct} tone={pctTone(pct, true)} />
        </div>
        <div className="mt-auto flex items-center justify-between pt-1 text-xs text-content-secondary">
          <span className="flex items-center gap-1">
            <i className="ri-team-line" /> {t("card.staff", { n: b.staffCount ?? 0 })}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-shape-line" /> {t("card.zones", { n: b.zones.length })}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line" /> {b.open24h ? "24/7" : `${b.openTime ?? ""}–${b.closeTime ?? ""}`}
          </span>
        </div>
      </div>
    </button>
  );
}

function BaseDetail({ base: b, staffNames, amenityNames, lng }: { base: Base; staffNames: Array<{ id: string; name: string; role: string; status: string }>; amenityNames: string[]; lng: string }) {
  const { t } = useTranslation("bases");
  const pct = occPct(b);
  const hasGeo = typeof b.latitude === "number" && typeof b.longitude === "number";
  return (
    <div className="space-y-5">
      <PhotoGallery photos={b.photos} alt={b.name} />

      <div className="grid grid-cols-3 gap-2">
        <Kpi icon="ri-pie-chart-2-line" label={t("kpi.occupancy")} value={`${pct}%`} progress={pct} tone={pctTone(pct, true)} />
        <Kpi icon="ri-team-line" label={t("kpi.staff")} value={staffNames.length} />
        <Kpi icon="ri-community-line" label={t("kpi.amenitiesShort")} value={amenityNames.length} tone="info" />
      </div>

      <div className="divide-y divide-line rounded-xl border border-line bg-card px-3">
        <DetailRow icon="ri-map-pin-line" label={t("fields.address")}>
          {[b.address, b.city, b.state, b.country].filter(Boolean).join(", ") || "—"}
        </DetailRow>
        <DetailRow icon="ri-time-line" label={t("fields.schedule")}>
          {b.open24h ? t("detail.open24h") : `${b.openTime ?? "—"} – ${b.closeTime ?? "—"}`} · {(b.days ?? []).map((d) => t(`weekdays.${d}`)).join(" ")}
        </DetailRow>
        <DetailRow icon="ri-stack-line" label={t("fields.capacity")}>
          {b.capacity.toLocaleString()} {b.capacityUnit}
        </DetailRow>
        <DetailRow icon="ri-user-star-line" label={t("fields.manager")}>
          <span className="flex items-center gap-2">
            {b.manager?.name ? <PersonAvatar name={b.manager.name} size="sm" /> : null}
            {b.manager?.name || "—"} {b.manager?.phone ? <span className="text-content-muted">· {b.manager.phone}</span> : null}
          </span>
        </DetailRow>
        {b.truckYardId ? (
          <DetailRow icon="ri-parking-box-line" label={t("detail.truckYard")}>
            {t("detail.truckYardLinked")}
          </DetailRow>
        ) : null}
        {b.notes ? (
          <DetailRow icon="ri-sticky-note-line" label={t("fields.notes")}>
            {b.notes}
          </DetailRow>
        ) : null}
        <DetailRow icon="ri-history-line" label={t("detail.updated")}>
          {typeof b.updatedAt === "string" ? relativeTime(b.updatedAt, lng) : "—"}
        </DetailRow>
      </div>

      {hasGeo ? (
        <div className="overflow-hidden rounded-xl border border-line">
          <SiteMap points={[{ id: b.id, lat: b.latitude as number, lng: b.longitude as number, label: b.name, color: TYPE_COLORS[b.type] }]} className="h-48 w-full" />
        </div>
      ) : null}

      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-content-secondary">{t("fields.zones")}</h3>
        {b.zones.length === 0 ? (
          <p className="text-xs text-content-muted">{t("form.noZones")}</p>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {b.zones.map((z) => (
              <div key={z.id} className="rounded-lg border border-line bg-card px-3 py-2">
                <div className="truncate text-sm font-medium text-content">{z.name}</div>
                <div className="text-[11px] text-content-muted">
                  {z.type} · {z.capacity.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-content-secondary">{t("detail.staff")}</h3>
        {staffNames.length === 0 ? (
          <p className="text-xs text-content-muted">{t("detail.noStaff")}</p>
        ) : (
          <ul className="space-y-1.5">
            {staffNames.map((s) => (
              <li key={s.id} className="flex items-center gap-2 text-sm text-content">
                <PersonAvatar name={s.name} size="sm" />
                <span className="truncate">{s.name}</span>
                <span className="ml-auto text-xs text-content-muted">{t(`roles.${s.role}`, { defaultValue: s.role })}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {amenityNames.length ? (
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-content-secondary">{t("kpi.amenitiesShort")}</h3>
          <div className="flex flex-wrap gap-1.5">
            {amenityNames.map((n) => (
              <Badge key={n} tone="info">
                {n}
              </Badge>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
